import { mainSiteUrl } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

interface ArticleData {
  slug: string;
  title: string;
  description: string;
  category: string;
  image: string;
  imageAlt: string;
  readTime: string;
  seoTitle: string;
  seoDescription: string;
  content: React.ReactNode;
}

export default function ArticleLayout({ article }: { article: ArticleData }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    image: article.image,
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a href="/" className="font-heading text-xl font-bold text-foreground-900 whitespace-nowrap cursor-pointer">
            AllChinaBuy<span className="text-primary-500">Spreadsheet</span>
          </a>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </div>
              Home
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5"
            >
              Buy It Now
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <article>
          <nav className="text-xs text-foreground-400 mb-6">
            <a href="/" className="hover:text-primary-500 transition-colors">Home</a>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">{article.title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 font-bold leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
              {article.description}
            </p>
            <div className="flex items-center gap-3 mt-5 text-xs text-foreground-400">
              <span>Updated: June 2026</span>
              <span>·</span>
              <span>{article.readTime} min read</span>
            </div>
          </header>

          <img
            src={article.image}
            alt={article.imageAlt}
            title={article.title}
            className="w-full rounded-2xl mb-10 object-cover"
            style={{ maxHeight: '400px' }}
          />

          <div className="prose-custom">
            {article.content}
          </div>

          <div className="mt-12 p-6 bg-primary-50 rounded-2xl border border-primary-100">
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong>Ready to start exploring?</strong> Head back to the{' '}
              <a href="/" className="text-primary-600 font-semibold hover:underline">
                AllChinaBuySpreadsheet homepage
              </a>{' '}
              to browse all 14 categories and discover the latest trusted finds, or visit{' '}
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-600 font-semibold hover:underline">
                kaspreadsheet.com
              </a>{' '}
              to start shopping now.
            </p>
          </div>
        </article>
      </main>

      <footer className="bg-foreground-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <p className="text-foreground-400 text-xs">
            © 2026 AllChinaBuySpreadsheet.asia —{' '}
            <a href="/" className="hover:text-white transition-colors">Back to Home</a>
          </p>
        </div>
      </footer>
    </>
  );
}